import { PlusOutlined } from '@ant-design/icons';
import {
  ModalForm,
  PageContainer,
  ProFormSelect,
  ProFormText,
  ProTable,
} from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Popconfirm } from 'antd';
import { useRef } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import {
  createTraceAccount,
  queryTraceAccounts,
  removeTraceAccount,
  updateTraceAccount,
} from '../services/trace';

const ACCOUNT_TYPE = {
  own: {
    text: '自有账号',
    status: 'Success',
  },
  rival: {
    text: '竞品账号',
    status: 'Processing',
  },
};

const PLATFORM = {
  douyin: {
    text: '抖音',
    status: 'Processing',
  },
  weibo: {
    text: '微博',
    status: 'Default',
  },
  xhs: {
    text: '小红书',
    status: 'Success',
  },
};

const accountTypeOptions = [
  {
    label: '自有账号',
    value: 'own',
  },
  {
    label: '竞品账号',
    value: 'rival',
  },
];

const platformOptions = [
  {
    label: '小红书',
    value: 'xhs',
  },
  {
    label: '抖音',
    value: 'douyin',
  },
  {
    label: '微博',
    value: 'weibo',
  },
];

const TraceAccountFormFields = () => (
  <>
    <ProFormSelect
      name="accountType"
      label="账号类型"
      options={accountTypeOptions}
      rules={[
        {
          required: true,
          message: '请选择账号类型',
        },
      ]}
    />
    <ProFormSelect
      name="platform"
      label="平台"
      options={platformOptions}
      rules={[
        {
          required: true,
          message: '请选择平台',
        },
      ]}
    />
    <ProFormText
      name="accountId"
      label="账号ID"
      placeholder="请输入平台侧账号ID"
      rules={[
        {
          required: true,
          message: '请输入账号ID',
        },
      ]}
    />
    <ProFormText
      name="name"
      label="账号名称"
      placeholder="请输入账号名称"
      rules={[
        {
          required: true,
          message: '请输入账号名称',
        },
      ]}
    />
  </>
);

const Trace = () => {
  const actionRef = useRef(null);
  const { organizationId } = useOrganizationPath();
  const { message } = App.useApp();

  const reload = () => {
    actionRef.current?.reload?.();
  };

  const { mutateAsync: createAccount, isPending: creating } = useMutation({
    mutationFn: createTraceAccount,
    onSuccess: () => {
      message.success('监测账号已创建');
      reload();
    },
    onError: () => {
      message.error('监测账号创建失败，请重试');
    },
  });
  const { mutateAsync: updateAccount, isPending: updating } = useMutation({
    mutationFn: updateTraceAccount,
    onSuccess: () => {
      message.success('监测账号已更新');
      reload();
    },
    onError: () => {
      message.error('监测账号更新失败，请重试');
    },
  });
  const { mutateAsync: removeAccount, isPending: removing } = useMutation({
    mutationFn: removeTraceAccount,
    onSuccess: () => {
      message.success('监测账号已移除');
      reload();
    },
    onError: () => {
      message.error('监测账号移除失败，请重试');
    },
  });

  const columns = [
    {
      title: '账号类型',
      dataIndex: 'accountType',
      valueEnum: ACCOUNT_TYPE,
    },
    {
      title: '平台',
      dataIndex: 'platform',
      valueEnum: PLATFORM,
    },
    {
      title: '账号ID',
      dataIndex: 'accountId',
      copyable: true,
    },
    {
      title: '账号名称',
      dataIndex: 'name',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => [
        <ModalForm
          key="edit"
          title="修改监测"
          trigger={<Button type="link">修改监测</Button>}
          width={480}
          initialValues={record}
          modalProps={{
            destroyOnHidden: true,
            okButtonProps: {
              loading: updating,
            },
          }}
          onFinish={async (values) => {
            try {
              await updateAccount({
                data: {
                  id: record.id,
                  organizationId,
                  ...values,
                },
              });
              return true;
            } catch {
              return false;
            }
          }}
        >
          <TraceAccountFormFields />
        </ModalForm>,
        <Popconfirm
          key="remove"
          title="移除监测"
          description={`确认移除 ${record.name}？`}
          okText="确认"
          cancelText="取消"
          onConfirm={() =>
            removeAccount({
              data: {
                id: record.id,
                organizationId,
              },
            })
          }
        >
          <Button type="link" danger loading={removing}>
            移除监测
          </Button>
        </Popconfirm>,
      ],
    },
  ];

  return (
    <PageContainer content="组织管理员在这里维护本组织需要持续监测的自有账号和竞品账号。">
      <ProTable
        actionRef={actionRef}
        rowKey="id"
        headerTitle="监测账号"
        request={(params) => queryTraceAccounts({ ...params, organizationId })}
        columns={columns}
        search={{
          labelWidth: 96,
        }}
        scroll={{
          x: 'max-content',
        }}
        toolBarRender={() => [
          <ModalForm
            key="create"
            title="创建监测"
            trigger={
              <Button type="primary" icon={<PlusOutlined />}>
                创建监测
              </Button>
            }
            width={480}
            modalProps={{
              destroyOnHidden: true,
              okButtonProps: {
                loading: creating,
              },
            }}
            onFinish={async (values) => {
              try {
                await createAccount({
                  data: {
                    organizationId,
                    ...values,
                  },
                });
                return true;
              } catch {
                return false;
              }
            }}
          >
            <TraceAccountFormFields />
          </ModalForm>,
        ]}
      />
    </PageContainer>
  );
};

export default Trace;
